import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { signIn, confirmSignIn, signUp, confirmSignUp, getCurrentUser, signOut } from 'aws-amplify/auth';
import { useAuth, setAuthFlag } from '../context/AuthContext.jsx';
import { useAppData } from '../context/AppDataContext.js';
import Header from './Header.js';
import { Footer } from './Footer.js';
import PhoneNumberInput from './PhoneNumberInput.js';
import SearchableServiceAreaSelect from './SearchableServiceAreaSelect.js';
import { withOfflineCheck, getUserFriendlyError } from '../utils/apiWrapper.js';
import { shouldBypassTestOtpAccount } from '../utils/testAccountBypass.js';
import { formatPhoneDisplay } from '../utils/phoneUtils.js';
import { convertArabicToEnglish } from '../utils/phoneUtils.js';
import {
  getDevqaOtpBypassClientMetadata,
  shouldAttemptDevqaOtpBypass,
  useDevqaOtpBypassVisible
} from '../utils/devqaOtpBypass.js';
import UserService from '../services/UserService.js';
import { writeStoredProfessionalMode } from '../utils/professionalModeStorage.js';
import ProfessionalService from '../services/ProfessionalService.js';
import LocationService from '../services/LocationService.js';
import ServiceAreaService from '../services/ServiceAreaService.js';
import { validateName } from '../utils/nameValidation.js';
import TermsConditionsModal from './TermsConditionsModal.js';
import { generateTempPassword } from '../utils/passwordGenerator.js';
import { clearAuthSessionCache } from '../config/amplifyConfig.js';
import ReferralService from '../services/ReferralService.js';
import { resendSignupOtp } from '../services/OtpService.js';
import { getProfessionalDefaultLandingPath } from '../utils/professionalLevel.js';
import { isUserDisabledError } from '../utils/disabledUserError.js';
import DisabledUserModal from './DisabledUserModal.js';
import WhatsAppOptInToggle from './WhatsAppOptInToggle.js';
import institutionService from '../services/InstitutionService.js';
import {
  INSTITUTIONS_ENABLED,
  INSTITUTION_HOME_PATH,
  pickPostLoginDestination,
  resolveInstitutionHomeIfAny,
} from '../utils/institutions.js';
import { useInstitution } from '../context/InstitutionContext.js';

const ONBOARDING_STORAGE_KEY = 'salehli_onboarding_state';
const RESEND_COOLDOWN = 60; // seconds
const ONBOARDING_TTL_MS = 15 * 60 * 1000; // 15 minutes

const Login = () => {
  const { checkUser, user, userProfile, loading } = useAuth();
  const { selectedCountry } = useAppData();
  const { isInstitution, loading: institutionLoading } = useInstitution();
  const router = useRouter();
  const { redirect, type } = router.query;
  const isProfessionalLogin = type === 'professional';
  const isCustomerLogin = type === 'customer';

  // Onboarding state
  // Steps: phone → otp → (accountType if new & no deep-link type) → name | join-craftsman
  const [onboardingStep, setOnboardingStep] = useState('phone'); // 'phone' | 'otp' | 'accountType' | 'name' | 'creating'
  const [onboardingData, setOnboardingData] = useState({
    userId: '',
    phoneNumber: '',
    otpVerified: false,
    fullName: '',
    city: '',
    neighborhood: '',
    customNeighborhood: '',
    tempPassword: '',
    lastResendAt: null,
    isNewUser: false, // Track if this is a new user registration
    isProfessional: false, // Track if this is professional registration
    expiresAt: null,
    referrerPhoneNumber: '',
    whatsappNotificationsEnabled: true
  });

  const isProfessionalMode = isProfessionalLogin || onboardingData.isProfessional;
  // Deep-link ?type= still skips the post-OTP account-type chooser
  const hasDeepLinkedLoginType = isProfessionalLogin || isCustomerLogin;
  const showAccountTypeChooser = onboardingStep === 'accountType';

  // UI state
  const [confirmationCode, setConfirmationCode] = useState('');
  const [devqaOtpBypassPassword, setDevqaOtpBypassPassword] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false); // New state for OTP verification
  const [error, setError] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showDisabledUserModal, setShowDisabledUserModal] = useState(false);
  const [showVideosModal, setShowVideosModal] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  // const [referrerSectionExpanded, setReferrerSectionExpanded] = useState(false);

  // Location data
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(true);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [loadingNeighborhoods, setLoadingNeighborhoods] = useState(false);

  // Field errors
  const [fieldErrors, setFieldErrors] = useState({});
  const showDevqaOtpBypass = useDevqaOtpBypassVisible();

  // Track if we've checked for incomplete profile to avoid duplicate checks
  const hasCheckedIncompleteProfile = useRef(false);

  // Load onboarding state from localStorage on mount
  useEffect(() => {
    const loadOnboardingState = () => {
      try {
        const saved = localStorage.getItem(ONBOARDING_STORAGE_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);

          // Expire onboarding state after TTL
          const expiresAt = typeof parsed.expiresAt === 'number' ? parsed.expiresAt : null;
          if (expiresAt && Date.now() > expiresAt) {
            localStorage.removeItem(ONBOARDING_STORAGE_KEY);
            return;
          }
          
          // Security check: If we have a phone number but no tempPassword, 
          // the state might be corrupted or from an old session - clear it
          if (parsed.phoneNumber && !parsed.tempPassword && parsed.isNewUser) {
            localStorage.removeItem(ONBOARDING_STORAGE_KEY);
            return;
          }
          
          // Check if there's a mismatch between onboarding phone and current user
          // This can happen when user switches between different phone numbers
          const checkPhoneMismatch = async () => {
            try {
              const currentUser = await getCurrentUser();
              const currentUserPhone = currentUser?.username || currentUser?.signInDetails?.loginId;
              
              if (currentUserPhone && parsed.phoneNumber && currentUserPhone !== parsed.phoneNumber) {
                localStorage.removeItem(ONBOARDING_STORAGE_KEY);
                return;
              }
            } catch (error) {
              // No current user - onboarding state is fine
            }
          };
          
          // Check for phone mismatch asynchronously
          checkPhoneMismatch();
          
          // Restore state
          setOnboardingData(prev => ({
            ...prev,
            ...parsed,
            // Ensure otpVerified is boolean
            otpVerified: parsed.otpVerified === true,
            whatsappNotificationsEnabled: parsed.whatsappNotificationsEnabled !== false,
            // Override isProfessional with query param if provided
            isProfessional: type === 'professional' || parsed.isProfessional || prev.isProfessional || false
          }));

          // Restore step, but enforce OTP verification for new users
          if (parsed.isNewUser) {
            // Only trust otpVerified if we also have a tempPassword (indicates valid session)
            if (parsed.otpVerified === true && parsed.tempPassword) {
              // OTP verified: never regress back to OTP on refresh
              if (parsed.step === 'creating') {
                setOnboardingStep('creating');
              } else if (parsed.step === 'accountType') {
                setOnboardingStep('accountType');
              } else if (
                parsed.step === 'name' ||
                type === 'customer' ||
                parsed.isProfessional ||
                type === 'professional'
              ) {
                setOnboardingStep('name');
              } else {
                // Verified but no account type chosen yet
                setOnboardingStep('accountType');
              }
            } else if (parsed.phoneNumber && parsed.tempPassword) {
              // Phone entered and account created but OTP not verified - force back to OTP
              setOnboardingStep('otp');
            } else {
              // No phone or incomplete state, start from beginning
              setOnboardingStep('phone');
            }
          } else {
            // Existing user - if OTP verified, should be logged in already
            // For existing users, always start at phone step initially
            // The incomplete profile check will move to 'name' step if needed
            setOnboardingStep('phone');
          }

          // Restore resend countdown if applicable
          if (parsed.lastResendAt) {
            const elapsed = Math.floor((Date.now() - parsed.lastResendAt) / 1000);
            const remaining = RESEND_COOLDOWN - elapsed;
            if (remaining > 0) {
              setResendCountdown(remaining);
            }
          }
        }
      } catch (error) {
        console.error('Error loading onboarding state:', error);
        localStorage.removeItem(ONBOARDING_STORAGE_KEY);
      }
    };

    loadOnboardingState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Save onboarding state to localStorage
  const saveOnboardingState = (updates) => {
    const now = Date.now();
    const updated = {
      ...onboardingData,
      ...updates,
      // Allow callers to override the persisted step explicitly
      step: updates.step || onboardingStep,
      // TTL-based expiration
      expiresAt: now + ONBOARDING_TTL_MS
    };
    setOnboardingData(updated);
    try {
      localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(updated));
    } catch (error) {
      console.error('Error saving onboarding state:', error);
    }
  };

  // Sync professional flag from deep-link ?type= only (do not clear when type is absent —
  // post-OTP account type selection stores isProfessional without a query param).
  useEffect(() => {
    if (!router.isReady) return;
    if (type === 'professional') {
      setOnboardingData(prev => ({
        ...prev,
        isProfessional: true
      }));
      saveOnboardingState({ isProfessional: true });
    } else if (type === 'customer') {
      setOnboardingData(prev => ({
        ...prev,
        isProfessional: false
      }));
      const saved = localStorage.getItem(ONBOARDING_STORAGE_KEY);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (parsed.isProfessional) {
            const updated = { ...parsed, isProfessional: false };
            localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(updated));
          }
        } catch (error) {
          console.error('Error updating onboarding state:', error);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, router.isReady]);

  // Check for incomplete profile when user is authenticated
  useEffect(() => {
    const checkIncompleteProfile = async () => {
      // Only check if user is authenticated
      if (!user) {
        hasCheckedIncompleteProfile.current = false;
        return;
      }

      // Skip if we're already in the middle of creating account
      if (onboardingStep === 'creating') {
        return;
      }

      // Skip if we're on OTP / account-type step and have a phone number (user is in active login flow)
      // But allow check if we're on phone step (user just landed on page)
      if (
        (onboardingStep === 'otp' || onboardingStep === 'accountType') &&
        onboardingData.phoneNumber
      ) {
        return;
      }

      // If we're already on 'name' step, only check if we haven't checked yet
      // This handles the case where localStorage restored us to 'name' step
      // but we need to verify the profile is actually incomplete
      if (onboardingStep === 'name' && hasCheckedIncompleteProfile.current) {
        return;
      }

      // Mark that we're checking to avoid duplicate checks
      hasCheckedIncompleteProfile.current = true;

      try {
        // Use userProfile from context if available, otherwise fetch
        let profile = userProfile;
        if (!profile || !profile.name) {
          // If userProfile doesn't have name, fetch fresh profile
          profile = await UserService.getCurrentUserProfile();
        }

        // Check if profile is incomplete (no name or empty name)
        // Handle both null/undefined and empty string cases
        const hasName = profile?.name && typeof profile.name === 'string' && profile.name.trim().length > 0;
        const isProfileIncomplete = !profile || !hasName;

        if (isProfileIncomplete) {
          // Check if user has a professional draft
          if (user.userId) {
            try {
              const professionalDraft = await ProfessionalService.loadApplicationDraft(user.userId);
              
              if (professionalDraft && professionalDraft.payload) {
                // User has professional draft - redirect to join-craftsman
                const redirectParam = redirect ? `?redirect=${encodeURIComponent(redirect)}` : '';
                router.push(`/join-craftsman${redirectParam}`);
                return;
              }
            } catch (draftError) {
              // No draft found - continue to show registration completion
            }
          }

          // No professional draft - restore onboarding state to name step
          // Get phone from user attributes or profile
          const phoneNumber = userProfile?.phone_number || 
                            profile?.phone_number ||
                            user.attributes?.phone_number || 
                            user.username || 
                            '';

          if (phoneNumber) {
            // Restore onboarding state to continue registration
            // Note: isNewUser is false because user already exists, just needs to complete profile
            // Clear any existing onboarding state first to ensure clean state
            saveOnboardingState({
              phoneNumber: phoneNumber,
              otpVerified: true, // OTP was already verified during login
              isNewUser: false, // User already exists, just needs to complete profile
              tempPassword: '', // Not needed for existing users
              fullName: '', // Clear any partial name
              city: '', // Clear any partial city
              neighborhood: '', // Clear any partial neighborhood
              customNeighborhood: '' // Clear any partial custom neighborhood
            });
            setOnboardingStep('name');
          }
        }
      } catch (error) {
        console.error('❌ Error checking incomplete profile:', error);
        // Reset flag on error so we can retry
        hasCheckedIncompleteProfile.current = false;
      }
    };

    // Add a small delay to ensure user and userProfile are fully loaded
    // Also wait a bit longer to ensure localStorage state has been processed
    const timer = setTimeout(() => {
      checkIncompleteProfile();
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userProfile]);

  // Clear onboarding state
  const clearOnboardingState = () => {
    setOnboardingData({
      userId: '',
      phoneNumber: '',
      otpVerified: false,
      fullName: '',
      city: '',
      neighborhood: '',
      customNeighborhood: '',
      tempPassword: '',
      lastResendAt: null,
      isNewUser: false,
      isProfessional: false,
      expiresAt: null,
      whatsappNotificationsEnabled: true
    });
    setConfirmationCode('');
    setDevqaOtpBypassPassword('');
    setResendCountdown(0);
    setError('');
    setFieldErrors({});
    setAcceptTerms(false);
    try {
      localStorage.removeItem(ONBOARDING_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing onboarding state:', error);
    }
  };

  // Countdown timer for resend functionality
  useEffect(() => {
    if (onboardingData.lastResendAt) {
      const interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - onboardingData.lastResendAt) / 1000);
        const remaining = RESEND_COOLDOWN - elapsed;
        setResendCountdown(remaining > 0 ? remaining : 0);
        if (remaining <= 0) clearInterval(interval);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [onboardingData.lastResendAt]);

  // Load cities on mount
  useEffect(() => {
    const loadCities = async () => {
      try {
        const citiesData = await LocationService.getCitiesByCountry('syria');
        setCities(citiesData || []);
      } catch (error) {
        console.error('Error loading cities:', error);
        setCities([]);
      } finally {
        setCitiesLoading(false);
      }
    };
    loadCities();
  }, []);

  // Load neighborhoods when city is selected (improved logic from RequestQuote)
  useEffect(() => {
    const loadNeighborhoods = async () => {
      if (onboardingData.city) {
        setLoadingNeighborhoods(true);
        try {
          // Exclude professionals_only areas for regular users
          const cityAreas = await ServiceAreaService.getServiceAreasByCity(selectedCountry || 'syria', onboardingData.city, true, true);
          setNeighborhoods(cityAreas);
        } catch (error) {
          console.error('Error loading neighborhoods:', error);
          setNeighborhoods([]);
        } finally {
          setLoadingNeighborhoods(false);
        }
      } else {
        setNeighborhoods([]);
      }
    };

    loadNeighborhoods();
  }, [onboardingData.city, selectedCountry]);

  // Handle custom neighborhood detection after neighborhoods are loaded (improved logic from RequestQuote)
  useEffect(() => {
    if (neighborhoods.length > 0 && onboardingData.neighborhood && onboardingData.neighborhood !== 'other') {
      // Check if the current neighborhood value is a valid area_code
      const isValidAreaCode = neighborhoods.some(area => area.area_code === onboardingData.neighborhood);
      
      if (!isValidAreaCode) {
        // This is a custom neighborhood, set it up properly
        saveOnboardingState({
          customNeighborhood: onboardingData.neighborhood,
          neighborhood: 'other'
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neighborhoods, onboardingData.neighborhood]);

  // Enforce OTP verification - prevent bypass
  useEffect(() => {
    // If user is on a post-OTP step but OTP not verified, force back to OTP
    if (
      (onboardingStep === 'name' || onboardingStep === 'accountType') &&
      !onboardingData.otpVerified
    ) {
      if (onboardingData.phoneNumber) {
        setOnboardingStep('otp');
      } else {
        setOnboardingStep('phone');
      }
    }
  }, [onboardingStep, onboardingData.otpVerified, onboardingData.phoneNumber]);

  // Redirect already authenticated users
  useEffect(() => {
    const redirectAuthenticatedUser = async () => {
      // Don't redirect while auth is still loading
      if (loading) {
        return;
      }

      // Only redirect if user is authenticated and not in the middle of onboarding
      if (!user || !userProfile) {
        return;
      }

      // Skip redirect if user is in the middle of completing their profile
      if (onboardingStep !== 'phone' || onboardingData.phoneNumber) {
        return;
      }

      // Skip redirect if profile is incomplete (no name)
      if (!userProfile.name || userProfile.name.trim() === '') {
        return;
      }

      // Wait for institution context only when the portal flag is on — no delay when off
      if (INSTITUTIONS_ENABLED && institutionLoading) {
        return;
      }

      const finalDestination = pickPostLoginDestination({
        redirect: typeof redirect === 'string' ? redirect : null,
        institutionHome: isInstitution ? INSTITUTION_HOME_PATH : null,
        professionalLandingPath: null,
      });
      
      console.log('User already authenticated, redirecting to:', finalDestination);
      router.push(finalDestination);
    };

    redirectAuthenticatedUser();
  }, [user, userProfile, loading, router, redirect, onboardingStep, onboardingData.phoneNumber, isInstitution, institutionLoading]);

  const resolvePostLoginRedirect = async () => {
    const requestedRedirect = typeof redirect === 'string' ? redirect : null;
    let destination = requestedRedirect || '/';

    try {
      // Profile + institution in parallel — no extra sequential round-trip
      const [currentProfile, institutionHome] = await Promise.all([
        UserService.getCurrentUserProfile(),
        resolveInstitutionHomeIfAny(() => institutionService.getMyInstitutionSecure()),
      ]);
      const professionalUserId =
        currentProfile?.user_id || currentProfile?.userId || currentProfile?.id || currentProfile?.sub;

      // Check if user profile is incomplete (no name)
      const isProfileIncomplete = !currentProfile || !currentProfile.name || currentProfile.name.trim() === '';

      if (isProfileIncomplete) {
        // Check if user has a professional draft (they might be in the middle of professional registration)
        if (professionalUserId) {
          try {
            const professionalDraft = await ProfessionalService.loadApplicationDraft(professionalUserId);
            
            if (professionalDraft && professionalDraft.payload) {
              // User has professional draft - redirect to join-craftsman to continue
              destination = '/join-craftsman';
              return destination;
            }
          } catch (draftError) {
            // No draft found or error loading draft - continue to check regular profile
          }
        }

        // No professional draft - user needs to complete regular registration
        // Redirect to login page which will detect incomplete profile and show name step
        destination = '/login';
        return destination;
      }

      let professionalLandingPath = null;
      const isProfessional = currentProfile?.roles?.includes('professional');
      if (isProfessional && typeof window !== 'undefined') {
        try {
          writeStoredProfessionalMode(true);
          const profProfile = await ProfessionalService.getCurrentProfessionalProfile();
          professionalLandingPath = getProfessionalDefaultLandingPath(profProfile);
        } catch (profErr) {
          professionalLandingPath = '/professional/profile';
        }
      }

      return pickPostLoginDestination({
        redirect: requestedRedirect,
        institutionHome,
        professionalLandingPath,
      });
    } catch (profileError) {
      console.error('Post-login redirect check failed:', profileError);
    }

    return destination;
  };

  // Step 1: Phone Number Submission - Try login first, then signup if user doesn't exist
  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setFieldErrors({});

    const phone = onboardingData.phoneNumber.trim();
    if (!phone) {
      setError('يرجى إدخال رقم الهاتف');
      setIsLoading(false);
      return;
    }

    try {
      // Check if there's already a signed-in user and clear the session if needed
      try {
        const currentUser = await getCurrentUser();
        if (currentUser) {
          const currentUserPhone = currentUser.username || currentUser.signInDetails?.loginId;
          
          // If the existing user is different from the one trying to log in, sign out
          if (currentUserPhone && currentUserPhone !== phone) {
            await signOut();
            clearAuthSessionCache();
            setAuthFlag(false);
            // Clear any conflicting onboarding state
            localStorage.removeItem(ONBOARDING_STORAGE_KEY);
            // Small delay to ensure signOut completes
            await new Promise(resolve => setTimeout(resolve, 500));
          } else if (currentUserPhone === phone) {
            // Same user is already signed in - redirect them to their dashboard
            const destination = await resolvePostLoginRedirect();
            clearOnboardingState();
            router.push(destination);
            return;
          }
        }
      } catch (checkUserError) {
        // No current user or error checking - continue with login flow
      }
      // For passwordless flow, always try signup first
      // If user exists, signup will fail with UsernameExistsException
      // If user doesn't exist, signup will succeed and send OTP
      const tempPassword = generateTempPassword();
      const isTestBypassAccount = shouldBypassTestOtpAccount('', phone);
      const devqaOtpBypassMetadata = getDevqaOtpBypassClientMetadata(devqaOtpBypassPassword);
      const shouldAttemptDevqaOtpBypassFlow = shouldAttemptDevqaOtpBypass(devqaOtpBypassPassword);
      
      let signUpResult;
      try {
        signUpResult = await withOfflineCheck(
          () => signUp({
            username: phone,
            password: tempPassword,
            options: {
              userAttributes: {
                phone_number: phone
              },
              ...(devqaOtpBypassMetadata ? { clientMetadata: devqaOtpBypassMetadata } : {})
            }
          }),
          { context: 'auth' }
        );
      } catch (signUpError) {
        // If user already exists, try to sign in with custom challenge
        if (signUpError.name === 'UsernameExistsException') {
          try {
            // Use CUSTOM_WITHOUT_SRP to bypass password verification and trigger custom challenge
            const { isSignedIn, nextStep } = await withOfflineCheck(
              () => signIn({
                username: phone,
                options: {
                  authFlowType: 'CUSTOM_WITHOUT_SRP',
                  ...(devqaOtpBypassMetadata ? { clientMetadata: devqaOtpBypassMetadata } : {})
                }
              }),
              { context: 'auth' }
            );

            if (isSignedIn) {
              // Test account bypass - signed in immediately
              clearAuthSessionCache();
              setAuthFlag(true);
              await checkUser();
              const destination = await resolvePostLoginRedirect();
              clearOnboardingState();
              router.push(destination);
              return;
            } else if (nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE' || 
                       nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_OTP') {
              if (shouldAttemptDevqaOtpBypassFlow) {
                let isBypassSignedIn = false;
                try {
                  const bypassResult = await withOfflineCheck(
                    () => confirmSignIn({
                      challengeResponse: devqaOtpBypassPassword.trim(),
                      options: {
                        clientMetadata: devqaOtpBypassMetadata
                      }
                    }),
                    { context: 'auth' }
                  );
                  isBypassSignedIn = Boolean(bypassResult?.isSignedIn);

                  if (isBypassSignedIn) {
                    clearAuthSessionCache();
                    setAuthFlag(true);
                    await checkUser();
                    const destination = await resolvePostLoginRedirect();
                    clearOnboardingState();
                    router.push(destination);
                    return;
                  }
                } catch (bypassError) {
                  console.warn('Devqa OTP bypass failed; falling back to OTP challenge', {
                    error: bypassError instanceof Error ? bypassError.message : String(bypassError),
                    hint:
                      'CloudWatch → bulkGateOtp Lambda → search DevqaOtpBypass rejected. Reasons: not_devqa_env | secret_not_configured | allowlist_empty | metadata_missing (Cognito did not pass clientMetadata) | phone_not_allowlisted | secret_mismatch. Set DEVQA_OTP_BYPASS_VERBOSE_LOG=true on the branch for CreateAuthChallenge diagnostics without typing كلمة السر.'
                  });
                }
                if (!isBypassSignedIn) {
                  console.info(
                    'Devqa OTP bypass did not sign you in — use SMS/WhatsApp code, or check CloudWatch bulkGateOtp logs (DevqaOtpBypass rejected) for devqaOtpBypassRejectReason.'
                  );
                }
              }

              // Existing user - OTP challenge required
              saveOnboardingState({
                phoneNumber: phone,
                isNewUser: false,
                lastResendAt: Date.now(),
                isProfessional: type === 'professional' || onboardingData.isProfessional
              });
              setDevqaOtpBypassPassword('');
              setOnboardingStep('otp');
              setResendCountdown(RESEND_COOLDOWN);
              setIsLoading(false);
              return;
            } else {
              // Sign in didn't trigger custom challenge - user likely has password
              setError('الحساب موجود بالفعل. للحسابات القديمة التي لديها كلمة مرور، يرجى استخدام خيار "نسيت كلمة المرور" لإعادة تعيينها، ثم يمكنك استخدام تسجيل الدخول بدون كلمة مرور في المستقبل.');
              setIsLoading(false);
              return;
            }
          } catch (signInError) {
            // Disabled Cognito user — show the suspended-account modal instead of
            // the misleading "wrong password / old account" errors below
            if (isUserDisabledError(signInError)) {
              setShowDisabledUserModal(true);
              setIsLoading(false);
              return;
            }

            // Handle "There is already a signed in user" error specifically
            if (signInError.message && signInError.message.includes('There is already a signed in user')) {
              setError('يوجد مستخدم مسجل دخول بالفعل. يرجى مسح حالة تسجيل الدخول والمحاولة مرة أخرى.');
              setIsLoading(false);
              return;
            }
            
            // If sign in fails, show helpful error
            if (signInError.name === 'NotAuthorizedException') {
              setError('الحساب موجود بالفعل. للحسابات القديمة التي لديها كلمة مرور، يرجى استخدام خيار "نسيت كلمة المرور" لإعادة تعيينها، ثم يمكنك استخدام تسجيل الدخول بدون كلمة مرور في المستقبل.');
            } else {
              const friendlyError = getUserFriendlyError(signInError, 'auth');
              setError(friendlyError.message);
            }
            setIsLoading(false);
            return;
          }
        }
        // Re-throw other errors
        throw signUpError;
      }
      
      const { isSignUpComplete, nextStep } = signUpResult;
      const signedUpUserId = signUpResult?.userId || signUpResult?.userSub || '';

      // Always require OTP verification unless the backend accepted a devqa/test bypass.
      if ((isTestBypassAccount || shouldAttemptDevqaOtpBypassFlow) && isSignUpComplete) {
        const skipAccountType = type === 'professional' || type === 'customer' || onboardingData.isProfessional;
        const nextStep = skipAccountType
          ? (type === 'professional' || onboardingData.isProfessional ? 'professional' : 'name')
          : 'accountType';
        saveOnboardingState({
          userId: signedUpUserId,
          phoneNumber: phone,
          tempPassword: tempPassword,
          otpVerified: true,
          isNewUser: true,
          isProfessional: type === 'professional' || onboardingData.isProfessional,
          step: nextStep
        });
        setDevqaOtpBypassPassword('');
        if (type === 'professional' || onboardingData.isProfessional) {
          // Mirror OTP success path for professionals
          try {
            await signIn({
              username: phone,
              password: tempPassword
            });
            clearAuthSessionCache();
            setAuthFlag(true);
            await checkUser();
          } catch (signInAfterBypassError) {
            console.error('Failed to sign in after signup bypass (professional flow):', signInAfterBypassError);
          }
          const redirectParam = redirect ? `?redirect=${encodeURIComponent(redirect)}` : '';
          router.push(`/join-craftsman${redirectParam}`);
          return;
        }
        setOnboardingStep(nextStep === 'professional' ? 'name' : nextStep);
      } else {
        // For all other cases, always require OTP verification
          saveOnboardingState({
            userId: signedUpUserId,
            phoneNumber: phone,
            tempPassword: tempPassword,
            otpVerified: false,
            isNewUser: true,
            isAutoConfirmed: isSignUpComplete,
            isProfessional: type === 'professional' || onboardingData.isProfessional,
            step: 'otp'
          });
        setOnboardingStep('otp');
        
        // If account was auto-confirmed, try to resend code
        if (isSignUpComplete) {
          try {
            await resendSignupOtp(phone);
            saveOnboardingState({ lastResendAt: Date.now() });
          } catch (resendError) {
            // Could not resend code for auto-confirmed account
          }
        }
      }
      setIsLoading(false);
      return;
    } catch (error) {
      console.error('Phone submission error:', error);

      if (isUserDisabledError(error)) {
        setShowDisabledUserModal(true);
        return;
      }

      // Handle "There is already a signed in user" error automatically
      if (error.message && error.message.includes('There is already a signed in user')) {
        try {
          await signOut();
          clearAuthSessionCache();
          setAuthFlag(false);
          localStorage.removeItem(ONBOARDING_STORAGE_KEY);
          
          // Small delay to ensure cleanup completes
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Retry the phone submission automatically
          return handlePhoneSubmit(e);
        } catch (clearError) {
          console.error('Error clearing session:', clearError);
          setError('حدث خطأ في تسجيل الدخول. يرجى إعادة تحميل الصفحة والمحاولة مرة أخرى.');
        }
      } else {
        const friendlyError = getUserFriendlyError(error, 'auth');
        setError(friendlyError.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: OTP Verification - Handle both login and signup
  const handleOtpVerify = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setIsVerifyingOtp(true);
    setError('');

    if (!confirmationCode.trim()) {
      setError('يرجى إدخال رمز التأكيد');
      setIsLoading(false);
      setIsVerifyingOtp(false);
      return;
    }

    try {
      if (onboardingData.isNewUser) {
        // New user - use confirmSignUp
        await confirmSignUp({
          username: onboardingData.phoneNumber,
          confirmationCode: confirmationCode.trim()
        });

        // OTP verified successfully
        setConfirmationCode('');

        const preferProfessional = onboardingData.isProfessional || type === 'professional';
        const preferCustomer = type === 'customer';

        // If professional registration (deep-link or already flagged), redirect to join-craftsman
        if (preferProfessional) {
          await continueProfessionalSignupAfterOtp();
          return;
        }

        // Deep-linked customer — skip account type chooser
        if (preferCustomer) {
          saveOnboardingState({
            otpVerified: true,
            isProfessional: false,
            step: 'name'
          });
          setOnboardingStep('name');
          return;
        }

        // New account with no type yet — ask after OTP (less friction for returning users)
        saveOnboardingState({
          otpVerified: true,
          step: 'accountType'
        });
        setOnboardingStep('accountType');
      } else {
        // Existing user - use confirmSignIn
        try {
          const { isSignedIn, nextStep } = await withOfflineCheck(
            () => confirmSignIn({
              challengeResponse: confirmationCode.trim()
            }),
            { context: 'auth' }
          );

          if (isSignedIn) {
            // Login successful
            clearAuthSessionCache();
            setAuthFlag(true);
            
            try {
              await checkUser();
              const destination = await resolvePostLoginRedirect();
              clearOnboardingState();
              router.push(destination);
              return;
            } catch (postLoginError) {
              console.error('Post-login processing failed:', postLoginError);
              
              // If it's a token-related error, try to refresh and retry once
              if (postLoginError.name === 'InvalidCharacterError' || 
                  postLoginError.message?.includes('atob')) {
                console.warn('Token error detected, attempting recovery');
                
                try {
                  // Clear cache and try again
                  clearAuthSessionCache();
                  await new Promise(resolve => setTimeout(resolve, 1000)); // Brief delay
                  await checkUser();
                  const destination = await resolvePostLoginRedirect();
                  clearOnboardingState();
                  router.push(destination);
                  return;
                } catch (retryError) {
                  console.error('Token recovery failed:', retryError);
                  setError('حدث خطأ أثناء تسجيل الدخول وتوجيهك إلى لوحة التحكم. يرجى المحاولة مرة أخرى أو الانتقال يدوياً إلى لوحة التحكم.');
                  setConfirmationCode('');
                  return;
                }
              }
              
              // For other errors, show generic error
              setError('حدث خطأ أثناء تسجيل الدخول وتوجيهك إلى لوحة التحكم. يرجى المحاولة مرة أخرى أو الانتقال يدوياً إلى لوحة التحكم.');
              setConfirmationCode('');
              return;
            }
          } else if (
            nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE' ||
            nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_OTP'
          ) {
            // Still in challenge loop → treat as wrong OTP and allow retry
            setError('رمز التأكيد غير صحيح. يرجى المحاولة مرة أخرى.');
            setConfirmationCode('');
          } else {
            setError('فشل التحقق من الرمز. يرجى المحاولة مرة أخرى.');
            setConfirmationCode('');
          }
        } catch (confirmError) {
          // If confirmSignIn fails because signIn wasn't called, re-initiate signIn
          if (confirmError.name === 'SignInException' || 
              (confirmError.message && confirmError.message.includes('signIn was not called'))) {
            // Re-initiate sign in to create session
            try {
              const { nextStep } = await withOfflineCheck(
                () => signIn({
                  username: onboardingData.phoneNumber,
                  options: {
                    authFlowType: 'CUSTOM_WITHOUT_SRP'
                  }
                }),
                { context: 'auth' }
              );
              
              if (nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE' || 
                  nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_OTP') {
                // Now try confirmSignIn again
                const { isSignedIn, nextStep: retryNextStep } = await withOfflineCheck(
                  () => confirmSignIn({
                    challengeResponse: confirmationCode.trim()
                  }),
                  { context: 'auth' }
                );
                
                if (isSignedIn) {
                  clearAuthSessionCache();
                  setAuthFlag(true);
                  
                  try {
                    await checkUser();
                    const destination = await resolvePostLoginRedirect();
                    clearOnboardingState();
                    router.push(destination);
                    return;
                  } catch (postLoginError) {
                    console.error('Post-login processing failed on retry:', postLoginError);
                    
                    // If it's a token-related error, try to refresh and retry once
                    if (postLoginError.name === 'InvalidCharacterError' || 
                        postLoginError.message?.includes('atob')) {
                      console.warn('Token error detected on retry, attempting recovery');
                      
                      try {
                        // Clear cache and try again
                        clearAuthSessionCache();
                        await new Promise(resolve => setTimeout(resolve, 1000)); // Brief delay
                        await checkUser();
                        const destination = await resolvePostLoginRedirect();
                        clearOnboardingState();
                        router.push(destination);
                        return;
                      } catch (retryError) {
                        console.error('Token recovery failed on retry:', retryError);
                        setError('حدث خطأ أثناء تسجيل الدخول وتوجيهك إلى لوحة التحكم. يرجى المحاولة مرة أخرى أو الانتقال يدوياً إلى لوحة التحكم.');
                        setConfirmationCode('');
                        return;
                      }
                    }
                    
                    // For other errors, show generic error
                    setError('حدث خطأ أثناء تسجيل الدخول وتوجيهك إلى لوحة التحكم. يرجى المحاولة مرة أخرى أو الانتقال يدوياً إلى لوحة التحكم.');
                    setConfirmationCode('');
                    return;
                  }
                }

                if (
                  retryNextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE' ||
                  retryNextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_OTP'
                ) {
                  setError('رمز التأكيد غير صحيح. يرجى المحاولة مرة أخرى.');
                  setConfirmationCode('');
                  return;
                }
              }
            } catch (retryError) {
              console.error('Failed to re-initiate sign in:', retryError);
              setError('انتهت صلاحية الجلسة. يرجى إعادة إدخال رقم الهاتف.');
              setOnboardingStep('phone');
              clearOnboardingState();
              return;
            }
          } else {
            throw confirmError;
          }
        }
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      if (isUserDisabledError(error)) {
        // Account was disabled by an administrator — not a wrong code
        setShowDisabledUserModal(true);
        setConfirmationCode('');
      } else if (error.name === 'CodeMismatchException') {
        setError('رمز التأكيد غير صحيح. يرجى المحاولة مرة أخرى.');
        setConfirmationCode(''); // Clear the input for retry
      } else if (error.name === 'ExpiredCodeException') {
        setError('انتهت صلاحية رمز التأكيد. يرجى طلب رمز جديد.');
        setConfirmationCode(''); // Clear the input for retry
      } else if (error.name === 'NotAuthorizedException') {
        // For OTP verification, NotAuthorizedException usually means wrong code
        // Check if this is during OTP verification (not initial sign-in)
        if (onboardingStep === 'otp') {
          setError('رمز التأكيد غير صحيح. يرجى المحاولة مرة أخرى.');
          setConfirmationCode(''); // Clear the input for retry
        } else {
          // For other cases, use the generic error
          const friendlyError = getUserFriendlyError(error, 'auth');
          setError(friendlyError.message);
        }
      } else if (onboardingData.isNewUser && (error.message?.includes('already confirmed') || error.message?.includes('User cannot be confirmed'))) {
        // Account is already confirmed (this can happen if Cognito auto-confirmed)
        // Since we just created the account in this session, we'll accept the OTP entry as verification
        saveOnboardingState({
          otpVerified: true
        });
        setConfirmationCode('');
        setOnboardingStep('name');
      } else {
        const friendlyError = getUserFriendlyError(error, 'auth');
        setError(friendlyError.message);
        // Clear input on any other error to allow retry
        if (onboardingStep === 'otp') {
          setConfirmationCode('');
        }
      }
    } finally {
      setIsLoading(false);
      setIsVerifyingOtp(false);
    }
  };

  // Resend OTP code
  const handleResendCode = async () => {
    if (!onboardingData.phoneNumber || resendCountdown > 0) return;
    
    setIsLoading(true);
    try {
      if (onboardingData.isNewUser) {
        // Re-send the persisted OTP without asking Cognito for a replacement code
        await resendSignupOtp(onboardingData.phoneNumber);
      } else {
        // Existing user - re-initiate sign in to trigger new OTP
        await withOfflineCheck(
          () => signIn({
            username: onboardingData.phoneNumber,
            options: {
              authFlowType: 'CUSTOM_WITHOUT_SRP'
            }
          }),
          { context: 'auth' }
        );
      }
      saveOnboardingState({ lastResendAt: Date.now() });
      setResendCountdown(RESEND_COOLDOWN);
      setError('');
    } catch (error) {
      console.error('Resend code error:', error);
      if (isUserDisabledError(error)) {
        setShowDisabledUserModal(true);
      } else {
        const friendlyError = getUserFriendlyError(error, 'auth');
        setError(friendlyError.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Name and Location Submission (combined for new users)
  const handleNameAndLocationSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setFieldErrors({});

    // Validate name
    const nameError = validateName(onboardingData.fullName);
    if (nameError) {
      setFieldErrors({ fullName: nameError });
      setError(nameError);
      setIsLoading(false);
      return;
    }

    // Validate city
    if (!onboardingData.city) {
      setFieldErrors({ city: 'يرجى اختيار المدينة' });
      setError('يرجى اختيار المدينة');
      setIsLoading(false);
      return;
    }

    // Validate terms acceptance
    if (!acceptTerms) {
      setFieldErrors({ acceptTerms: 'يجب الموافقة على الشروط والأحكام' });
      setError('يجب الموافقة على الشروط والأحكام');
      setIsLoading(false);
      return;
    }

    // Save state and proceed to account creation
    saveOnboardingState({ fullName: onboardingData.fullName.trim() });
    setOnboardingStep('creating');
    await handleAccountCreation();
  };

  // Step 5: Account Creation (for new users) or Profile Completion (for existing users)
  const handleAccountCreation = async () => {
    try {
      // For new users, sign in with temp password first
      if (onboardingData.isNewUser && onboardingData.tempPassword) {
        await signIn({
          username: onboardingData.phoneNumber,
          password: onboardingData.tempPassword
        });
        
        // Set auth flag immediately
        clearAuthSessionCache();
        setAuthFlag(true);
      }
      // For existing users, they're already signed in, just update profile

      // Get the current onboarding state to ensure we have the latest custom neighborhood
      const currentState = JSON.parse(localStorage.getItem(ONBOARDING_STORAGE_KEY) || '{}');
      const customNeighborhood = currentState.customNeighborhood || onboardingData.customNeighborhood || '';
      
      const neighborhoodValue = (onboardingData.neighborhood === 'other' || currentState.neighborhood === 'other')
        ? (customNeighborhood || onboardingData.customNeighborhood || '')
        : (onboardingData.neighborhood || currentState.neighborhood || '');

      // Ensure we have a valid neighborhood value (don't save empty string for custom neighborhoods)
      const finalNeighborhoodValue = neighborhoodValue.trim() || '';

      // Create or update user profile
      try {
        await UserService.createUserProfile({
          name: onboardingData.fullName,
          phoneNumber: onboardingData.phoneNumber,
          city: onboardingData.city,
          neighborhood: finalNeighborhoodValue,
          country: 'syria',
          whatsappNotificationsEnabled: onboardingData.whatsappNotificationsEnabled !== false,
          notification_preferences: {
            sms: onboardingData.whatsappNotificationsEnabled !== false,
            push: true
          }
        });
      } catch (profileError) {
        // Profile might already exist, try to update it
        if (profileError.errors && profileError.errors[0]?.errorType === 'DynamoDB:ConditionalCheckFailedException') {
          try {
            await UserService.updateUserProfile({
              name: onboardingData.fullName,
              city_code: onboardingData.city,
              neighborhood_code: finalNeighborhoodValue,
              notification_preferences: {
                sms: onboardingData.whatsappNotificationsEnabled !== false,
                push: true
              }
            });
          } catch (updateError) {
            console.error('Error updating user profile:', updateError);
            throw updateError;
          }
        } else {
          console.error('Error creating user profile:', profileError);
          throw profileError;
        }
      }

      await checkUser();

      if ((onboardingData.referrerPhoneNumber || '').trim()) {
        ReferralService.submitReferral(onboardingData.referrerPhoneNumber).catch(() => {});
      }
      
      // Clear onboarding state
      clearOnboardingState();
      
      // Redirect
      const destination = await resolvePostLoginRedirect();
      router.push(destination);
    } catch (error) {
      console.error('Account creation error:', error);
      if (isUserDisabledError(error)) {
        setShowDisabledUserModal(true);
      } else {
        const friendlyError = getUserFriendlyError(error, 'auth');
        setError(friendlyError.message);
      }
      setOnboardingStep('name');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle back to edit phone
  const handleBackToPhone = () => {
    clearOnboardingState();
    setOnboardingStep('phone');
    setConfirmationCode('');
    setError('');
  };

  // After OTP: continue professional signup (shared by deep-link and post-OTP chooser)
  const continueProfessionalSignupAfterOtp = async () => {
    // Keep onboarding state (temp password + verified OTP) so we can resume if user navigates away.
    // Also sign-in immediately so user doesn't get stuck re-doing OTP later.
    saveOnboardingState({
      otpVerified: true,
      isProfessional: true,
      step: 'professional'
    });

    try {
      if (onboardingData.tempPassword) {
        await signIn({
          username: onboardingData.phoneNumber,
          password: onboardingData.tempPassword
        });
        clearAuthSessionCache();
        setAuthFlag(true);
        await checkUser();
      }
    } catch (signInAfterOtpError) {
      // If sign-in fails, still allow join-craftsman (it can sign-in later using tempPassword)
      console.error('Failed to sign in after OTP (professional flow):', signInAfterOtpError);
    }

    const redirectParam = redirect ? `?redirect=${encodeURIComponent(redirect)}` : '';
    router.push(`/join-craftsman${redirectParam}`);
  };

  const handleSelectAccountType = async (accountType) => {
    setError('');
    if (accountType === 'professional') {
      setIsLoading(true);
      try {
        await continueProfessionalSignupAfterOtp();
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Customer signup — name + city + terms
    saveOnboardingState({
      otpVerified: true,
      isProfessional: false,
      step: 'name'
    });
    setOnboardingStep('name');
    if (router.isReady) {
      router.replace(buildLoginHref('customer'), undefined, { shallow: true });
    }
  };

  // Render step components
  const renderPhoneStep = () => (
    <form onSubmit={handlePhoneSubmit} className="space-y-6">
      <div>
        <PhoneNumberInput
          value={onboardingData.phoneNumber}
          onChange={(phone) => {
            saveOnboardingState({ phoneNumber: phone });
            if (fieldErrors.phoneNumber) {
              setFieldErrors(prev => ({ ...prev, phoneNumber: '' }));
            }
          }}
          onBlur={(phone) => {
            if (!phone.trim()) {
              setFieldErrors(prev => ({ ...prev, phoneNumber: 'يرجى إدخال رقم الهاتف' }));
            }
          }}
          label="رقم الواتساب"
          required={true}
          defaultCountry="syria"
          placeholder="رقم هاتفك بدون صفر"
          className="mb-0"
          error={fieldErrors.phoneNumber}
        />
      </div>

      {showDevqaOtpBypass && (
        <input
          type="password"
          id="devqaOtpBypassPassword"
          value={devqaOtpBypassPassword}
          onChange={(e) => {
            setDevqaOtpBypassPassword(e.target.value);
            if (error) setError('');
          }}
          autoComplete="off"
          aria-label="كلمة السر"
          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="كلمة السر"
        />
      )}

      <button
        type="submit"
        disabled={isLoading || !onboardingData.phoneNumber.trim()}
        className="w-full py-3 px-4 rounded-md focus:outline-none focus:ring-2 disabled:opacity-50 transition-colors font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:ring-blue-500"
      >
        {isLoading ? 'جاري الإرسال...' : 'إرسال رمز التحقق'}
      </button>
    </form>
  );

  const renderOtpStep = () => {
    // Show skeleton loader during OTP verification
    if (isVerifyingOtp) {
      return (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <p className="text-sm text-gray-600 dark:text-slate-400 mb-2">
              تم إرسال رمز التحقق عبر رسالة <strong>واتس اب</strong>
            </p>
            <p 
              className="font-medium text-gray-900 dark:text-slate-100 text-lg tracking-wide"
              dir="ltr"
              style={{ unicodeBidi: 'plaintext' }}
            >
              {formatPhoneDisplay(onboardingData.phoneNumber)}
            </p>
          </div>

          {/* Skeleton loader for verification */}
          <div className="space-y-4">
            <div className="text-center">
              <div className="inline-flex items-center space-x-2 space-x-reverse">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="text-blue-600 dark:text-blue-400 font-medium">
                  جاري التحقق من الرمز...
                </span>
              </div>
            </div>
            
            {/* Skeleton input */}
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-24 mb-2"></div>
              <div className="h-12 bg-gray-200 dark:bg-slate-700 rounded-md"></div>
            </div>
            
            {/* Skeleton buttons */}
            <div className="flex justify-center space-x-4 space-x-reverse animate-pulse">
              <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-20"></div>
              <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-1"></div>
              <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-24"></div>
            </div>
            
            {/* Skeleton submit button */}
            <div className="animate-pulse">
              <div className="h-12 bg-gray-200 dark:bg-slate-700 rounded-md"></div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <form onSubmit={handleOtpVerify} className="space-y-6">
        <div className="text-center mb-6">
          <p className="text-sm text-gray-600 dark:text-slate-400 mb-2">
            تم إرسال رمز التحقق عبر رسالة <strong>واتس اب</strong>
          </p>
          <p 
            className="font-medium text-gray-900 dark:text-slate-100 text-lg tracking-wide"
            dir="ltr"
            style={{ unicodeBidi: 'plaintext' }}
          >
            {formatPhoneDisplay(onboardingData.phoneNumber)}
          </p>
          <p className="text-xs text-gray-500 dark:text-slate-400 mt-2">
            إذا لم يصلك الرمز خلال دقيقة، يمكنك طلب إعادة الإرسال
          </p>
        </div>

        <div>
          <label htmlFor="confirmationCode" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
            رمز التأكيد *
          </label>
          <input
            type="text"
            id="confirmationCode"
            value={confirmationCode}
            onChange={(e) => {
              const convertedValue = convertArabicToEnglish(e.target.value);
              const value = convertedValue.replace(/\D/g, '');
              setConfirmationCode(value.slice(0, 6));
              if (error) setError('');
            }}
            required
            maxLength={6}
            inputMode="numeric"
            autoComplete="one-time-code"
            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-2xl tracking-widest"
            placeholder="000000"
            dir="ltr"
          />
          <p className="text-xs text-gray-600 dark:text-slate-400 mt-1">أدخل الرمز المكون من 6 أرقام المرسل إلى رقمك الوتس اب</p>
        </div>

        <div className="flex justify-center space-x-4 space-x-reverse">
          <button
            type="button"
            onClick={handleResendCode}
            disabled={isLoading || resendCountdown > 0}
            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {resendCountdown > 0 ? `إعادة الإرسال خلال ${resendCountdown}s` : 'إعادة إرسال الرمز'}
          </button>
          <span className="text-gray-300 dark:text-slate-600">|</span>
          <button
            type="button"
            onClick={handleBackToPhone}
            className="text-sm text-gray-600 dark:text-slate-400 hover:text-gray-500 dark:hover:text-slate-300"
          >
            تغيير رقم الهاتف
          </button>
        </div>

        <button
          type="submit"
          disabled={isLoading || !confirmationCode.trim() || confirmationCode.trim().length !== 6}
          className="w-full py-3 px-4 rounded-md focus:outline-none focus:ring-2 disabled:opacity-50 transition-colors font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:ring-blue-500"
        >
          {isLoading ? 'جاري التحقق...' : 'تأكيد الرمز'}
        </button>
      </form>
    );
  };

  const renderNameAndLocationStep = () => (
    <form onSubmit={handleNameAndLocationSubmit} className="space-y-6">
      <div>
        <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
          الاسم الكامل *
        </label>
        <input
          type="text"
          id="fullName"
          value={onboardingData.fullName}
          onChange={(e) => {
            saveOnboardingState({ fullName: e.target.value });
            if (fieldErrors.fullName) {
              setFieldErrors(prev => ({ ...prev, fullName: '' }));
            }
          }}
          onBlur={(e) => {
            const nameError = validateName(e.target.value);
            if (nameError) {
              setFieldErrors(prev => ({ ...prev, fullName: nameError }));
            }
          }}
          required
          className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            fieldErrors.fullName ? 'border-red-500 focus:border-red-500' : 'border-gray-300 dark:border-slate-600 focus:border-blue-500'
          }`}
          placeholder="أدخل الاسم والكنية (مثال: أحمد فتال)"
        />
        {fieldErrors.fullName && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center">
            <span className="ml-1">⚠️</span>
            {fieldErrors.fullName}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="city" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
          المدينة *
        </label>
        <select
          id="city"
          value={onboardingData.city}
          onChange={(e) => {
            saveOnboardingState({
              city: e.target.value,
              neighborhood: '',
              customNeighborhood: ''
            });
            if (fieldErrors.city) {
              setFieldErrors(prev => ({ ...prev, city: '' }));
            }
          }}
          required
          disabled={citiesLoading}
          className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right ${
            citiesLoading
              ? 'bg-gray-100 dark:bg-slate-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              : fieldErrors.city
              ? 'border-red-500 focus:border-red-500'
              : 'border-gray-300 dark:border-slate-600 focus:border-blue-500'
          }`}
          style={{
            MozAppearance: 'none',
            WebkitAppearance: 'none',
            appearance: 'none'
          }}
        >
          <option value="">
            {citiesLoading ? 'جاري التحميل...' : 'اختر المدينة'}
          </option>
          {cities.map((cityOption) => (
            <option key={cityOption.city_code} value={cityOption.city_code}>
              {cityOption.name_ar || cityOption.name_en || cityOption.city_code}
            </option>
          ))}
        </select>
        {fieldErrors.city && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center">
            <span className="ml-1">⚠️</span>
            {fieldErrors.city}
          </p>
        )}
      </div>

      {onboardingData.city && (
        <div>
          <label htmlFor="neighborhood" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
            الحي <span className="text-gray-400 dark:text-slate-500 font-normal">(اختياري)</span>
          </label>
          <SearchableServiceAreaSelect
            countryCode={selectedCountry || 'syria'}
            cityCode={onboardingData.city}
            value={onboardingData.neighborhood}
            onChange={(areaCode) => {
              // When selecting 'other', preserve the custom neighborhood name that was just set
              // When selecting a regular area, clear custom neighborhood
              // Read from localStorage to get the latest custom neighborhood value (in case state hasn't updated yet)
              const currentState = JSON.parse(localStorage.getItem(ONBOARDING_STORAGE_KEY) || '{}');
              const currentCustomNeighborhood = currentState.customNeighborhood || onboardingData.customNeighborhood || '';
              
              saveOnboardingState({
                neighborhood: areaCode,
                customNeighborhood: areaCode !== 'other' ? '' : currentCustomNeighborhood
              });
            }}
            allowCustomOption
            customOptionLabel="الحي غير موجود في القائمة؟ أضف حي أخر"
            customSelectionLabel={onboardingData.neighborhood === 'other' 
              ? (onboardingData.customNeighborhood || '') 
              : undefined}
            onCustomAreaAdd={(label) => {
              saveOnboardingState({ customNeighborhood: label ? label.trim() : '' });
            }}
            placeholder="ابحث عن الحي أو المنطقة..."
            disabled={!onboardingData.city || loadingNeighborhoods}
          />
        </div>
      )}

      {/* Optional referrer — hidden for now
      <div className="border border-gray-200 dark:border-slate-600 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setReferrerSectionExpanded((e) => !e)}
          className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-right text-sm font-medium text-gray-700 dark:text-slate-300 bg-gray-50 dark:bg-slate-800/50 hover:bg-gray-100 dark:hover:bg-slate-800"
        >
          <span>رقم الشخص الذي دعاك؟ (اختياري)</span>
          <span className="shrink-0 transition-transform" style={{ transform: referrerSectionExpanded ? 'rotate(180deg)' : 'none' }}>
            ▼
          </span>
        </button>
        {referrerSectionExpanded && (
          <div className="px-3 pb-3 pt-1 border-t border-gray-200 dark:border-slate-600">
            <PhoneNumberInput
              value={onboardingData.referrerPhoneNumber}
              onChange={(phone) => saveOnboardingState({ referrerPhoneNumber: phone })}
              label=""
              required={false}
              defaultCountry="syria"
              placeholder="رقم موبايل الشخص الذي دعاك"
              className="mb-0"
            />
          </div>
        )}
      </div>
      */}

      <WhatsAppOptInToggle
        enabled={onboardingData.whatsappNotificationsEnabled !== false}
        onChange={(enabled) => saveOnboardingState({ whatsappNotificationsEnabled: enabled })}
        idPrefix="login-whatsapp-opt-in"
      />

      <div>
        <div className="flex items-start space-x-2 space-x-reverse">
          <input
            type="checkbox"
            id="acceptTerms"
            checked={acceptTerms}
            onChange={(e) => {
              setAcceptTerms(e.target.checked);
              if (fieldErrors.acceptTerms) {
                setFieldErrors(prev => ({ ...prev, acceptTerms: '' }));
              }
            }}
            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="acceptTerms" className="text-sm text-gray-700 dark:text-slate-300">
            أوافق على{' '}
            <button
              type="button"
              onClick={() => setShowTermsModal(true)}
              className="text-blue-600 dark:text-blue-400 hover:underline"
            >
              الشروط والأحكام
            </button>
            {' '}وسياسة الخصوصية
          </label>
        </div>
        {fieldErrors.acceptTerms && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center">
            <span className="ml-1">⚠️</span>
            {fieldErrors.acceptTerms}
          </p>
        )}
      </div>

        <button
        type="submit"
        disabled={isLoading || !onboardingData.fullName.trim() || !onboardingData.city || !acceptTerms}
        className="w-full py-3 px-4 rounded-md focus:outline-none focus:ring-2 disabled:opacity-50 transition-colors font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:ring-blue-500"
      >
        {isLoading ? 'جاري الحفظ...' : 'إنشاء الحساب'}
      </button>
    </form>
  );

  const renderCreatingStep = () => (
    <div className="text-center space-y-4">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      <p className="text-gray-600 dark:text-slate-400">جاري إنشاء حسابك...</p>
    </div>
  );

  const buildLoginHref = (loginType) => {
    const params = new URLSearchParams();
    if (loginType) {
      params.set('type', loginType);
    }
    if (redirect) {
      params.set('redirect', String(redirect));
    }
    const qs = params.toString();
    return qs ? `/login?${qs}` : '/login';
  };

  const renderAccountTypeChooser = () => (
    <div className="space-y-4 animate-fade-in">
      <button
        type="button"
        onClick={() => handleSelectAccountType('customer')}
        disabled={isLoading}
        className="group w-full rounded-2xl border-2 border-blue-200 dark:border-blue-700 bg-gradient-to-l from-blue-600 to-indigo-600 p-5 text-right text-white shadow-sm transition-all hover:from-blue-700 hover:to-indigo-700 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900 disabled:opacity-50"
        aria-label="دخول الزبائن"
      >
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="text-2xl font-extrabold leading-tight sm:text-3xl">دخول الزبائن</div>
            <p className="mt-1 text-sm font-medium text-blue-100">
              زبائن بحاجة للخدمات
            </p>
          </div>
          <span className="text-2xl opacity-80 transition-transform group-hover:-translate-x-1" aria-hidden="true">
            ←
          </span>
        </div>
      </button>

      <button
        type="button"
        onClick={() => handleSelectAccountType('professional')}
        disabled={isLoading}
        className="group w-full rounded-2xl border-2 border-salehli-yellow-400 dark:border-salehli-yellow-500 bg-salehli-yellow-500 p-5 text-right text-salehli-blue-900 shadow-sm transition-all hover:bg-salehli-yellow-400 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-salehli-yellow-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900 disabled:opacity-50"
        aria-label="دخول المهنيين"
      >
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="text-2xl font-extrabold leading-tight sm:text-3xl">دخول المهنيين</div>
            <p className="mt-1 text-sm font-medium text-salehli-blue-800/80">
              للحرفيين ومقدمي الخدمات
            </p>
          </div>
          <span className="text-2xl opacity-80 transition-transform group-hover:-translate-x-1" aria-hidden="true">
            ←
          </span>
        </div>
      </button>
    </div>
  );

  const pageTitle = showAccountTypeChooser
    ? 'حدد نوع الحساب'
    : isProfessionalMode
      ? 'دخول المهنيين'
      : hasDeepLinkedLoginType || onboardingStep === 'name' || onboardingStep === 'creating'
        ? 'دخول الزبائن'
        : 'تسجيل الدخول';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <Header />
      
      {/* Show loading while checking authentication */}
      {(loading || !router.isReady) && (
        <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8">
            <div className="bg-white dark:bg-slate-900 py-8 px-6 shadow-sm rounded-lg">
              <div className="text-center">
                <div className="inline-flex items-center space-x-2 space-x-reverse">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span className="text-gray-600 dark:text-slate-400">
                    جاري التحقق من حالة تسجيل الدخول...
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Show login form only when not loading */}
      {!loading && router.isReady && (
        <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8">
            <div>
              <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-slate-100">
                {pageTitle}
              </h2>
              {showAccountTypeChooser && (
                <p className="mt-3 text-center text-base text-gray-600 dark:text-slate-400">
                  اختر نوع الحساب للمتابعة
                </p>
              )}
            </div>

            <div className="bg-white dark:bg-slate-900 py-8 px-6 shadow-sm rounded-2xl">
            {error && (
              <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 rounded">
                <div className="mb-2">{error}</div>
                {error.includes('توجيهك إلى لوحة التحكم') && (
                  <div className="mt-3 pt-3 border-t border-red-300 dark:border-red-600">
                    <p className="text-sm mb-2">يمكنك الانتقال يدوياً إلى:</p>
                    <div className="flex flex-wrap gap-2">
                      {isProfessionalMode ? (
                        <>
                          <Link href="/professional/jobs" className="inline-flex items-center px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition-colors">
                            لوحة تحكم المهنيين
                          </Link>
                          <Link href="/professional/profile" className="inline-flex items-center px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition-colors">
                            الملف الشخصي
                          </Link>
                        </>
                      ) : (
                        <>
                          <Link href="/dashboard" className="inline-flex items-center px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition-colors">
                            لوحة التحكم
                          </Link>
                          <Link href="/profile" className="inline-flex items-center px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition-colors">
                            الملف الشخصي
                          </Link>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {showAccountTypeChooser ? (
              renderAccountTypeChooser()
            ) : (
              <>
                {onboardingStep === 'phone' && renderPhoneStep()}
                {onboardingStep === 'otp' && renderOtpStep()}
                {onboardingStep === 'name' && renderNameAndLocationStep()}
                {onboardingStep === 'creating' && renderCreatingStep()}

                {/* Explainer videos — single link opens chooser modal */}
                {onboardingStep === 'phone' && (
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-slate-700">
                    <button
                      type="button"
                      onClick={() => setShowVideosModal(true)}
                      className="w-full text-center text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900 rounded-md py-2"
                      aria-label="فيديوهات توضيحية"
                    >
                      🎥 فيديوهات توضيحية
                    </button>
                  </div>
                )}
              </>
            )}
            </div>
          </div>
        </div>
      )}

      <Footer />

      {/* Explainer videos chooser modal */}
      {showVideosModal && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowVideosModal(false);
          }}
        >
          <div
            className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-sm w-full p-6 relative"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="videos-modal-title"
          >
            <button
              type="button"
              onClick={() => setShowVideosModal(false)}
              className="absolute top-4 left-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
              aria-label="إغلاق"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <h3
              id="videos-modal-title"
              className="text-center text-lg font-bold text-gray-900 dark:text-slate-100 mb-5"
            >
              🎥 فيديوهات توضيحية
            </h3>

            <div className="flex flex-col gap-3">
              <Link
                href="/videos/users"
                onClick={() => setShowVideosModal(false)}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 font-semibold transition-colors text-white shadow-sm bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-400"
                aria-label="فيديو توضيحي للزبائن"
              >
                <span>للزبائن</span>
                <span aria-hidden="true">↗</span>
              </Link>
              <Link
                href="/videos/professionals"
                onClick={() => setShowVideosModal(false)}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 font-semibold transition-colors text-white shadow-sm bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-400"
                aria-label="فيديو توضيحي للمهنيين"
              >
                <span>للمهنيين</span>
                <span aria-hidden="true">↗</span>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Terms & Conditions Modal */}
      <TermsConditionsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />

      {/* Disabled Cognito user — suspended account modal */}
      <DisabledUserModal
        isOpen={showDisabledUserModal}
        onClose={() => setShowDisabledUserModal(false)}
        phoneNumber={onboardingData.phoneNumber}
      />
    </div>
  );
};

export default Login;
